/**
 * CreateKartotekaInline — inline panel tworzenia nowej kartoteki (encji) w edytorze szablonu.
 *
 * Renderowany w prawej kolumnie ModuleTemplateEditor zamiast modala. Logika identyczna
 * z trybem create z EntityFormModal (picker systemowych + kreatorowych + pola nowej).
 *
 * Props:
 *  - templateSlug:  slug bieżącego szablonu (wykluczamy z pickera kreatorowych).
 *  - existingSlugs: slugi już istniejących encji (walidacja unikalności).
 *  - onSubmit:      callback z tablicą encji do dodania (BEZ wywołań API — zapis przez rodzica).
 *  - onClose:       callback zamknięcia panelu (przycisk „Anuluj").
 *  - readOnly:      gdy true, blokuje submit (panel wyrenderowany przez rodzica jako read-only).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdminGlobalDocumentType,
  type DocumentClass,
  emptyListConfig,
  type ListConfig,
  MODULE_TEMPLATE_SLUG_PATTERN,
  type ModuleTemplateEntityDefinition,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { api } from '@/lib/api';

/**
 * 7 klas dokumentów (kanonicznie `DocumentClass` z shared). Kolejność = kolejność w selekcie.
 * Slug klasy ≠ slug modułu dokumentowego z GET /admin/kartoteki (tam pluralizowany:
 * `purchase_invoices`), dlatego strategia `document_class` używa tej stałej — wartość
 * musi być kanoniczną klasą (param `?class=` typów globalnych + pole `documentClass`).
 */
const DOCUMENT_CLASS_VALUES: readonly DocumentClass[] = [
  'purchase_invoice',
  'sale_invoice',
  'contract',
  'letter',
  'commercial',
  'internal',
  'specjalne',
];

// ---------------------------------------------------------------------------
// Helpery (lustro z ModuleTemplateEditor.tsx — nie eksportowane tam)
// ---------------------------------------------------------------------------

function slugify(input: string): string {
  const ascii = input.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ł/g, 'l');
  const cleaned = ascii.replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  const trimmed = cleaned.replace(/^[^a-z]+/, '');
  return trimmed;
}

function isValidSlug(slug: string): boolean {
  return MODULE_TEMPLATE_SLUG_PATTERN.test(slug);
}

// ---------------------------------------------------------------------------
// Kształt danych z GET /admin/kartoteki
// ---------------------------------------------------------------------------

interface KartotekaListItem {
  name: string;
  slug?: string;
  source: string;
  division: string;
  classes?: { name: string; slug: string }[];
  editTarget?: { templateCode: string; entitySlug: string } | null;
  published?: boolean;
}

// ---------------------------------------------------------------------------
// Komponent
// ---------------------------------------------------------------------------

export interface CreateKartotekaInlineProps {
  /** Slug bieżącego szablonu — wykluczany z pickera kreatorowych (nie możemy odwoływać się sami do siebie). */
  templateSlug: string;
  /** Slugi encji już obecnych w szablonie — walidacja unikalności + disabled checkbox. */
  existingSlugs: string[];
  /** Callback po zbudowaniu listy encji — BEZ API, rodzic robi upsert + zapis. */
  onSubmit: (entities: ModuleTemplateEntityDefinition[]) => void;
  /** Callback zamknięcia panelu (Anuluj lub po submit — rodzic decyduje). */
  onClose: () => void;
  /** Konto bez roli projektanta — blokuje submit. */
  readOnly?: boolean;
}

export function CreateKartotekaInline({
  templateSlug,
  existingSlugs,
  onSubmit,
  onClose,
  readOnly = false,
}: CreateKartotekaInlineProps) {
  const { t } = useTranslation();

  // Pola nowej kartoteki (new_table)
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  // Picker systemowych
  const [systemSlugs, setSystemSlugs] = useState<string[]>([]);

  // Picker kreatorowych: klucz = `${templateCode}::${entitySlug}`
  const [creatorKeys, setCreatorKeys] = useState<string[]>([]);

  // Kartoteka na bazie klasy dokumentu (document_class): wybrana klasa + systemowy typ.
  const [docClass, setDocClass] = useState<DocumentClass | ''>('');
  const [docTypeId, setDocTypeId] = useState('');

  // Query pickera — panel jest mountowany tylko gdy aktywny, więc enabled: true
  const systemKartotekiQuery = useQuery({
    queryKey: ['admin', 'kartoteki'],
    queryFn: async () =>
      (await api.get<{ kartoteki: KartotekaListItem[] }>('/admin/kartoteki')).data,
    retry: false,
  });

  const allKartoteki = systemKartotekiQuery.data?.kartoteki ?? [];

  // Top-level systemowe + klasy dokumentów (flatMap z wpisu division='klasy')
  const systemKartoteki = [
    ...allKartoteki
      .filter((k) => k.source === 'systemowa' && k.division !== 'klasy' && !!k.slug)
      .map((k) => ({ slug: k.slug as string, name: k.name })),
    ...allKartoteki
      .filter((k) => k.source === 'systemowa' && k.division === 'klasy')
      .flatMap((k) => k.classes ?? [])
      .map((c) => ({ slug: c.slug, name: c.name })),
  ];

  // Kreatorowe — tylko opublikowane, nie ten sam szablon
  const creatorKartoteki = allKartoteki
    .filter(
      (k) =>
        k.source === 'kreator' &&
        k.published === true &&
        k.editTarget &&
        k.editTarget.templateCode !== templateSlug,
    )
    .map((k) => ({
      key: `${k.editTarget!.templateCode}::${k.editTarget!.entitySlug}`,
      templateCode: k.editTarget!.templateCode,
      entitySlug: k.editTarget!.entitySlug,
      name: k.name,
    }));

  // Systemowe (globalne) typy wybranej klasy — tylko opublikowane. Enabled dopiero po
  // wyborze klasy (bez klasy nie ma o co pytać).
  const globalTypesQuery = useQuery({
    queryKey: ['admin', 'global-document-types', docClass, 'published'],
    queryFn: async () =>
      (
        await api.get<{ types: AdminGlobalDocumentType[] }>(
          `/admin/global-document-types?class=${docClass}&status=published`,
        )
      ).data,
    enabled: docClass !== '',
    retry: false,
  });
  const globalTypes = globalTypesQuery.data?.types ?? [];

  // Logika stanu (lustro EntityFormModal create-mode)
  const isSystemPick = systemSlugs.length > 0 || creatorKeys.length > 0;
  // Tryb „na bazie klasy dokumentu" aktywny gdy wybrano klasę. Wyklucza się z pickerem
  // gotowych/kreatorowych kartotek (albo referencja, albo klasa dokumentu).
  const isDocumentClassMode = docClass !== '';
  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid = isValidSlug(effectiveSlug);
  const slugUnique = !existingSlugs.includes(effectiveSlug);
  const baseFieldsValid = Boolean(name) && slugValid && slugUnique;
  // Gdy klasa wybrana — typ systemowy też jest wymagany (inaczej kartoteka nie wie do
  // czego zawęzić dokumenty).
  const docClassValid = !isDocumentClassMode || docTypeId !== '';
  const canSubmit = !readOnly && (isSystemPick ? true : baseFieldsValid && docClassValid);

  const toggleSystem = (s: string) =>
    setSystemSlugs((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const toggleCreator = (key: string) =>
    setCreatorKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const changeDocClass = (value: string) => {
    setDocClass((value as DocumentClass) || '');
    setDocTypeId(''); // reset typu przy zmianie klasy (typy są per-klasa)
  };

  const submit = () => {
    if (!canSubmit) return;

    if (isSystemPick) {
      const refs: ModuleTemplateEntityDefinition[] = systemSlugs.map((s, i) => {
        const sk = systemKartoteki.find((k) => k.slug === s);
        return {
          slug: s,
          name: sk ? sk.name : s,
          sortOrder: i,
          navigationMode: 'tab',
          ddlStrategy: 'reference_to_system_entity',
          referenceSystemModuleSlug: s,
          icon: undefined,
          fields: [],
          views: [
            {
              slug: 'form',
              name: t('referenceFormBuilder.defaultViewName'),
              type: 'form' as const,
              isDefault: true,
              sortOrder: 0,
            },
            {
              slug: 'list',
              name: t('referenceFormBuilder.defaultListViewName'),
              type: 'list' as const,
              isDefault: true,
              sortOrder: 1,
              listConfig: {
                ...emptyListConfig,
                columns: [{ id: 'record_name', visible: true, width: 200, order: 0 }],
              } satisfies ListConfig,
            },
          ],
        };
      });

      // Kreatorowe (Etap 3)
      const seenCreatorSlugs = new Set(refs.map((r) => r.slug));
      const creatorRefs: ModuleTemplateEntityDefinition[] = [];
      creatorKeys.forEach((key) => {
        const ck = creatorKartoteki.find((k) => k.key === key);
        if (!ck) return;
        const refSlug = ck.entitySlug;
        if (seenCreatorSlugs.has(refSlug) || existingSlugs.includes(refSlug)) return;
        seenCreatorSlugs.add(refSlug);
        creatorRefs.push({
          slug: refSlug,
          name: ck.name,
          sortOrder: refs.length + creatorRefs.length,
          navigationMode: 'tab',
          ddlStrategy: 'reference_to_creator_entity',
          referenceTemplateCode: ck.templateCode,
          referenceTargetEntitySlug: ck.entitySlug,
          icon: undefined,
          fields: [],
          views: [],
        });
      });

      onSubmit([...refs, ...creatorRefs]);
      return;
    }

    // Na bazie klasy dokumentu — rekordy trafiają do wspólnej tabeli `documents`
    // (bez nowej tabeli), zawężone do wybranej klasy i systemowego typu.
    if (isDocumentClassMode) {
      onSubmit([
        {
          slug: effectiveSlug,
          name,
          sortOrder: 0,
          navigationMode: 'tab',
          ddlStrategy: 'document_class',
          documentClass: docClass,
          documentGlobalTypeId: docTypeId,
          icon: undefined,
          fields: [],
          views: [],
        },
      ]);
      return;
    }

    onSubmit([
      {
        slug: effectiveSlug,
        name,
        sortOrder: 0,
        navigationMode: 'tab',
        ddlStrategy: 'new_table',
        icon: undefined,
        fields: [],
        views: [],
      },
    ]);
  };

  return (
    // min-w-0 — konieczne by 1fr track gridu nie kolapsował (wzorzec MenuBuilderEmbedded)
    <div className="min-w-0" data-testid="create-kartoteka-inline">
      <h2 className="mb-4 text-lg font-semibold">{t('moduleTemplateEditor.newEntity')}</h2>

      <div className="flex flex-col gap-4">
        {/* Picker systemowych */}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text-secondary">
            {t('moduleTemplateEditor.systemPick.pickLabel')}
          </span>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {systemKartoteki.map((k) => {
              const already = existingSlugs.includes(k.slug);
              return (
                <Checkbox
                  key={k.slug}
                  label={
                    already
                      ? `${k.name} — ${t('moduleTemplateEditor.systemPick.alreadyAdded')}`
                      : k.name
                  }
                  checked={systemSlugs.includes(k.slug)}
                  disabled={already || readOnly || isDocumentClassMode}
                  onChange={() => toggleSystem(k.slug)}
                />
              );
            })}
          </div>
          {systemKartotekiQuery.isLoading && (
            <span className="text-xs text-text-muted">{t('common.loading')}</span>
          )}
          <span className="text-xs text-text-muted">
            {t('moduleTemplateEditor.systemPick.hint')}
          </span>
        </div>

        {/* Picker kreatorowych (Etap 3) — tylko gdy są do wyboru */}
        {creatorKartoteki.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="creator-kartoteki-pick">
            <span className="text-sm font-medium text-text-secondary">
              {t('moduleTemplateEditor.creatorPick.pickLabel')}
            </span>
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {creatorKartoteki.map((k) => {
                const already = existingSlugs.includes(k.entitySlug);
                return (
                  <Checkbox
                    key={k.key}
                    label={
                      already
                        ? `${k.name} (${k.templateCode}) — ${t('moduleTemplateEditor.systemPick.alreadyAdded')}`
                        : `${k.name} (${k.templateCode})`
                    }
                    checked={creatorKeys.includes(k.key)}
                    disabled={already || readOnly || isDocumentClassMode}
                    onChange={() => toggleCreator(k.key)}
                  />
                );
              })}
            </div>
            <span className="text-xs text-text-muted">
              {t('moduleTemplateEditor.creatorPick.hint')}
            </span>
          </div>
        )}

        {/* Na bazie klasy dokumentu (document_class) — klasa + systemowy typ */}
        <div className="flex flex-col gap-2" data-testid="document-class-pick">
          <span className="text-sm font-medium text-text-secondary">
            {t('moduleTemplateEditor.documentClassPick.pickLabel')}
          </span>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label={t('moduleTemplateEditor.documentClassPick.classLabel')}>
              <Dropdown
                placeholder={t('moduleTemplateEditor.documentClassPick.classPlaceholder')}
                // Pusta wartość → `undefined`, żeby Radix pokazał placeholder
                // (parytet z dawną pustą opcją `disabled` natywnego selecta).
                value={docClass === '' ? undefined : docClass}
                disabled={readOnly || isSystemPick}
                onChange={(v) => changeDocClass(v)}
                options={DOCUMENT_CLASS_VALUES.map((c) => ({
                  value: c,
                  label: t(`adminDocumentTypes.classes.${c}`),
                }))}
              />
            </FormField>
            {isDocumentClassMode && (
              <FormField label={t('moduleTemplateEditor.documentClassPick.typeLabel')}>
                <Dropdown
                  placeholder={t('moduleTemplateEditor.documentClassPick.typePlaceholder')}
                  value={docTypeId === '' ? undefined : docTypeId}
                  disabled={readOnly}
                  onChange={(v) => setDocTypeId(v)}
                  options={globalTypes.map((gt) => ({ value: gt.id, label: gt.name }))}
                />
              </FormField>
            )}
          </div>
          {isDocumentClassMode && globalTypesQuery.isLoading && (
            <span className="text-xs text-text-muted">{t('common.loading')}</span>
          )}
          {isDocumentClassMode && !globalTypesQuery.isLoading && globalTypes.length === 0 && (
            <span className="text-xs text-amber-600">
              {t('moduleTemplateEditor.documentClassPick.typeEmpty')}
            </span>
          )}
          <span className="text-xs text-text-muted">
            {t('moduleTemplateEditor.documentClassPick.hint')}
          </span>
        </div>

        {/* Separator */}
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-text-muted">
          <span className="h-px flex-1 bg-border-primary" />
          {t('moduleTemplateEditor.systemPick.orNew')}
          <span className="h-px flex-1 bg-border-primary" />
        </div>

        {/* Pola nowej kartoteki */}
        <Input
          label={t('moduleTemplateEditor.field.name')}
          value={isSystemPick ? '' : name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('moduleTemplateEditor.field.entityNamePlaceholder')}
          disabled={isSystemPick || readOnly}
        />
        <Input
          label={t('moduleTemplateEditor.field.slug')}
          hint={t('moduleTemplateEditor.field.slugHint')}
          value={isSystemPick ? '' : effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={
            !isSystemPick && effectiveSlug && !slugValid
              ? t('moduleTemplateEditor.field.slugInvalid')
              : !isSystemPick && effectiveSlug && !slugUnique
                ? t('moduleTemplateEditor.field.slugTaken')
                : undefined
          }
          placeholder="np. wnioski"
          disabled={isSystemPick || readOnly}
        />

        {/* Przyciski */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}
